import invariant from "invariant";

import { getProductBadgeVariant } from "@theme/components";
import { darkTheme, lightTheme } from "@theme/themes";
import {
  NavCategory,
  NavCategoryColumn,
  NavProduct,
  NavQueryCategory,
  NavQueryCategoryColumn,
  NavQueryProduct,
  NavSubCategory,
  NavigationStaticText,
  SiteNavData,
  SiteNavQueryData,
} from "@theme/types";

import {
  LocaleHeadlineGroupProps,
  StrapiBlogAsset,
  StrapiHeadlineProps,
  StrapiLocaleAttributes,
  StrapiLocaleHeadlineProps,
} from "@ms/gatsby-site/src/types/strapi";

import { strapiLanguagesArray } from "@ms/gatsby-site/src/config/global";

// BaseUrl needed for some blog links
const baseUrl = "https://www.manscaped.com";

export const pluralize = (number: number | string, singular: string, plural: string): string => {
  if (typeof number === "string") {
    number = parseInt(number);
  }
  return number === 1 ? singular : plural;
};

export const truncateString = (string: string, limit: number) => {
  if (string.length > limit) {
    return `${string.substring(0, limit)}...`;
  }
  return string;
};

/**
 * URL-Encode a Javascript object
 *
 * @param data
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const encode = (data: any) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

/**
 * Scroll To Id
 *
 * For accessibility its best to scroll and focus on element
 *
 * this function allows us to scroll to the element and focus after scroll is completed
 * by adding an event listener and on completion adding a tabindex to the element,
 * focus on the element
 * and remove the tabindex as cleanup
 *
 * @param id - elements id to scroll to
 * @param isNavbarHidden - Navigation is disabled
 * @param offset - If we need to scroll lower on page, add offset
 */
export function scrollToId(id: string, isNavbarHidden = false, offset = 0) {
  const element = document.getElementById(id);
  if (!element) return;
  const elemTop = element.getBoundingClientRect().top;
  // NavHeight is dependent on window width with the desktop being larger (category bar shown on desktop)
  const expandedNavHeight = window.innerWidth >= 1024 ? 157 : 98;
  const collapsedNavHeight = window.innerWidth >= 1024 ? 115 : 46;
  const bannerHeight = isNavbarHidden
    ? 0
    : elemTop > 0
    ? collapsedNavHeight + offset
    : expandedNavHeight + offset;
  const top = elemTop + window.scrollY - bannerHeight;
  const fixedOffset = top.toFixed();
  const onScroll = function () {
    if (window.scrollY.toFixed() === fixedOffset) {
      window.removeEventListener("scroll", onScroll);
      element.tabIndex = -1;
      element.focus({ preventScroll: true });
      window.scrollTo({ top: top }); // safari scrolls when element focuses, override by scrolling again
      element.removeAttribute("tabindex"); // remove focus thats adding outline on element
    }
  };

  window.addEventListener("scroll", onScroll);
  onScroll();
  window.scrollTo({
    top: Number(fixedOffset),
    behavior: "smooth",
  });
}

/**
 * Wait Till True
 *
 * To prevent race conditions we sometimes need to check until one function is true
 * to run another function
 * if the first function returns true, run callback function,
 * else set timeout and try again
 * exponentially increasing the timeout delay
 * after 5 retries, give up
 *
 * Note: delay defaults to 100 and increases exponentially;
 *
 * @param {() => boolean} boolFunc
 * @param {() => void} callback
 * @param {Number} delay
 */
export const waitTillTrue = (
  boolFunc: () => boolean,
  callback: () => void,
  fallback?: () => void,
) => {
  let retryCount = 1;
  let delay = 100;
  const scheduler = (boolFunc: () => boolean, callback: () => void, fallback?: () => void) => {
    if (boolFunc() === true) {
      callback();
    } else if (retryCount <= 5) {
      setTimeout(() => {
        scheduler(boolFunc, callback, fallback);
      }, delay);
      delay *= 2;
      retryCount += 1;
    } else {
      fallback && fallback();
    }
  };
  scheduler(boolFunc, callback, fallback);
};

/**
 * Remove gid from Shopify ID
 * for Product/product variant Shopify ID
 */
export const removeGidFromShopifyId = (id?: string) => {
  if (!id) return undefined;
  if (id.includes(`gid://`)) {
    return id.replace(/\D/g, "");
  } else {
    return id;
  }
};
/**
 * Turn 123456778 or gid://shopify/ProductVariant/123456778
 * --> gid://shopify/ProductVariant/123456778
 *
 * for Product Variant ID
 */
export const addGidToProductVariantId = (variantId: string) => {
  return variantId.includes(`gid://shopify/ProductVariant/`)
    ? variantId
    : `gid://shopify/ProductVariant/${variantId}`;
};

/*
 * Handle Country Redirect Click
 *
 * We need to check if the corresponding site has the same
 * URL
 * We first fetch the testUrl and see if it returns a status of
 * 200
 * If yes, we redirect users to the same url,
 *
 * else we redirect users to the home page of the corresponding site
 *
 * @param baseUrl baseurl of redirect instance
 * @param pathname url of current page
 */
export const handleCountryRedirectClick = async (baseUrl: string, pathname: string) => {
  const testUrl = `${baseUrl}${pathname}?redirect=1`;
  const fallbackUrl = `${baseUrl}/?redirect=1`;

  const fetchStatus = await fetch(testUrl)
    .then((response) => {
      return response.status;
    })
    .catch((error) => console.error(error));
  if (fetchStatus === 200) {
    window.location.assign(testUrl);
  } else {
    window.location.assign(fallbackUrl);
  }
};

/**
 * Optimize Cloudinary Secure URL
 *
 * This function takes a Cloudinary secure URL and adds
 * the f_auto and q_auto parameters to it
 *
 * graphql does not include this as secure_url is the source and not the optimized version
 *
 * regex below is to check if the url already has f_auto or q_auto after /upload/
 *
 * @param {string} src
 * @returns {string} optimized source
 */
export const optimizeCloudinarySecureUrl = (src: string) => {
  const regex = /\/upload\/(?!f_auto|q_auto)/;
  return src.replace(regex, "/upload/f_auto,q_auto/");
};

/**
 * Get Navigation SubCategories
 *
 * This function return an array of all the navigation sub categories that exist in a column.
 * Useful for when we are getting the full list of a columns sub categories, like in the collection pages.
 *
 * @param {NavCategory} category
 * @returns {NavSubCategory[]} subCategories
 */
export const getNavigationSubCategories = (category: NavCategory): NavSubCategory[] => {
  return category.navColumns.reduce(
    (subCategories: NavSubCategory[], navCategoryColumn: NavCategoryColumn | null) => {
      const subCategoryItems = navCategoryColumn?.items;
      if (subCategoryItems) {
        return [...subCategories, ...subCategoryItems];
      } else {
        return subCategories;
      }
    },
    [],
  );
};

/**
 * Get Internal or External Link
 *
 * Some areas, like the navigation featured information, may rarely link externally
 * We need to determine whether to append the base url to that link for blog support
 *
 * @param {string | null} slug
 * @param {boolean} isBlog
 * @returns {string | null} link
 */
export const getInternalOrExternalLink = (slug?: string | null, isBlog = false) => {
  if (slug) {
    const internal = slug.startsWith("/");
    if (internal) {
      return isBlog ? `${baseUrl}${slug}` : slug;
    } else {
      return slug;
    }
  }
  return null;
};

/**
 * Get Promo Banner Link
 *
 * This function takes in a string of markdown content and returns the link in the content.
 * If there is no link in the content, it returns null.
 * Necessary because we have to make the whole super banner clickable.
 *
 * @param {string} markdownText
 * @param {boolean} isBlog
 * @returns {string | null} link
 */
const getPromoBannerLink = (markdownText: string, isBlog: boolean): string | null => {
  const regex = /\[(.*?)\]\((.*?)\)/;
  const match = markdownText.match(regex);
  const link = match && match[2];
  if (link) {
    return getInternalOrExternalLink(link, isBlog);
  } else {
    return link;
  }
};

/**
 * Get Localized Nav Data
 *
 * This function massages the data received from strapi in our useNavData & useBlogNavData hooks.
 * It lives here so that we can use the same function for both the general sites and the blog, rather than
 * duplicating code between the two hooks. Also deals with handling inactive products and other exceptions.
 *
 * @param {SiteNavQueryData} siteNavData
 * @param {string} language
 * @param {string} currentMsMarket
 * @param {boolean} isBlog
 * @param {NavigationStaticText} navigationStaticText
 * @returns {SiteNavData} localizedSiteNavData
 */
export const getLocalizedNavData = (
  siteNavData: SiteNavQueryData,
  shopAllCategory: NavQueryCategory,
  language: string,
  currentMsMarket: string,
  isBlog: boolean,
  navigationStaticText: NavigationStaticText,
  strapiBlogAsset?: StrapiBlogAsset,
): SiteNavData => {
  invariant(siteNavData, "Navigation Data is Required");

  // The following fields will be transformed / localized and returned
  const { categories, promoBanner, localizations } = siteNavData;

  const localizedNavigationInformation = getStrapiLocaleContent(language, localizations);

  // Get Localized Navigation Category Content - also adds additional page slug property,
  // which links to the category's collection page
  const getLocalizedNavCategory = (category: NavQueryCategory): NavCategory | null => {
    const { navColumnOne, navColumnTwo, navColumnThree, navColumnFour, clpHeroImage } = category;
    const localizedCategory = getStrapiLocaleContent(language, category.localizations);
    const backCTAText = navigationStaticText.back;

    const localizedTitle = localizedCategory?.title ?? category.title;
    const shopAllCTAText = `${navigationStaticText.shopAll} ${localizedTitle}`;

    const localizedNavColumnOne = getLocalizedNavColumn(navColumnOne);
    const localizedNavColumnTwo = getLocalizedNavColumn(navColumnTwo);
    const localizedNavColumnThree = getLocalizedNavColumn(navColumnThree);
    const localizedNavColumnFour = getLocalizedNavColumn(navColumnFour);

    const featuredImageModule =
      // If we are in blog and strapiBlogAsset FeaturedImage exists, use that.
      // Setting some dummy data here to retain the same shape as FeaturedImageModule
      isBlog && strapiBlogAsset?.featuredImage
        ? {
            ...strapiBlogAsset.featuredImage,
            shopNowText: navigationStaticText.shopNow,
            sectionName: "BlogFeaturedImage",
            product: null,
            showFeaturedImage: true,
            locale: "en",
            localizations: { data: [] },
          }
        : // Featured Image Module only queried on gatsby-site - will be null for blog
        category.featuredImageModule
        ? {
            ...category.featuredImageModule,
            shopNowText: navigationStaticText.shopNow,
          }
        : null;
    const localeClpHeroImage = localizedCategory?.clpHeroImage.data.attributes ?? clpHeroImage;

    // Localized Navigation Category Content
    const localizedNavCategory = {
      ...category,
      isStaticLink: false,
      // Page slug links to the category's collection page - if blog, include the baseUrl
      pageSlug: isBlog
        ? `${baseUrl}/collections/${category.slug}`
        : `/collections/${category.slug}`,
      backCTAText: backCTAText,
      shopAllCTAText: shopAllCTAText,
      featuredImageModule: featuredImageModule,
      title: localizedTitle,
      subtitle: localizedCategory?.subtitle ?? category.subtitle,
      eyebrow: localizedCategory?.eyebrow ?? category.eyebrow,
      navColumns: [
        ...(localizedNavColumnOne ? [localizedNavColumnOne] : []),
        ...(localizedNavColumnTwo ? [localizedNavColumnTwo] : []),
        ...(localizedNavColumnThree ? [localizedNavColumnThree] : []),
        ...(localizedNavColumnFour ? [localizedNavColumnFour] : []),
      ],
      clpHeroImage: localeClpHeroImage,
    };
    // Don't return any category that does not have a slug - else it will link to nothing
    return localizedNavCategory.slug && localizedNavCategory.navColumns.length > 0
      ? localizedNavCategory
      : null;
  };

  // Get Localized Navigation Column Content - also accounts for edge cases with inactive products or those without slugs
  const getLocalizedNavColumn = (
    column: NavQueryCategoryColumn | null,
  ): NavCategoryColumn | null => {
    if (column) {
      const localizedNavColumn = {
        id: column.id,
        items: column.items
          ?.map((item) => {
            const localizedItem = getStrapiLocaleContent(language, item.localizations);
            const shopAllText = navigationStaticText.shopAll;
            const localizedTitle = localizedItem?.title ?? item.title;
            const shopAllCTAText = `${shopAllText} ${localizedTitle}`;
            // Get Product List - if getLocalizedProduct returns null
            // (if the product is inactive or does not have a slug), we don't add that product.
            const products = item.products.reduce(
              (localizedProducts: NavProduct[], product: NavQueryProduct) => {
                const localizedProduct = getLocalizedProduct(product);
                if (localizedProduct) {
                  return [...localizedProducts, localizedProduct];
                } else {
                  return localizedProducts;
                }
              },
              [],
            );
            return {
              ...item,
              title: localizedTitle,
              subtitle: localizedItem?.subtitle ?? item.subtitle,
              shopAllText: shopAllCTAText,
              products: products,
            };
          })
          // Account for edge case, where products array has zero length (when it has only inactive products or products without slugs).
          // Also ensures SubCategory has a slug associated with it - otherwise, the collection page links to null.
          // This ensures that we don't return anything for column items since there are no products to display.
          .filter((item) => item.products.length > 0 && item.slug),
      };
      // Strapi can allow us to have an empty items array, in which case there's no column data - return null in this case so we don't render anything
      return localizedNavColumn.items.length > 0 ? localizedNavColumn : null;
    } else {
      return null;
    }
  };

  // Get Localized Product - Returns the localized product. Returns null if product is inactive or does not have a slug.
  const getLocalizedProduct = (product: NavQueryProduct): NavProduct | null => {
    const productLocalizations = getStrapiLocaleContent(language, product.localizations);

    const productBadge = getProductBadgeVariant(product.productBadges, currentMsMarket);

    const localizedProduct = {
      ...product,
      title: productLocalizations?.title ?? product?.title,
      subtitle: productLocalizations?.subtitle ?? product?.subtitle,
      // Page slug links to the product's PDP - if blog, include the baseUrl
      pageSlug: isBlog ? `${baseUrl}/products/${product.slug}` : `/products/${product.slug}`,
      productBadge: productBadge,
    };

    // If a product is active and has a slug, we should show in the navigation - otherwise, remove
    const displayProduct = localizedProduct.active && Boolean(localizedProduct.slug);

    return displayProduct ? localizedProduct : null;
  };

  // Remove any category that may be null (has no sub categories or slug)
  const navigationCategories = categories.reduce(
    (categories: NavCategory[], category: NavQueryCategory | null) => {
      const localizedCategory = category && getLocalizedNavCategory(category);
      if (localizedCategory && localizedCategory.slug !== shopAllCategory.slug) {
        return [...categories, localizedCategory];
      } else {
        return categories;
      }
    },
    [],
  );

  invariant(navigationCategories.length > 0, "Navigation categories are required.");

  // Shop All Category - This is a special category that links to the shop all page - it has to be separate from the other categories
  // in order to be able to filter out categories that don't have any active products
  const localizedShopAllCategory = getStrapiLocaleContent(language, shopAllCategory.localizations);

  const shopAllStaticCategory: NavCategory = {
    ...shopAllCategory,
    isStaticLink: true,
    pageSlug: isBlog
      ? `${baseUrl}/collections/${shopAllCategory.slug}`
      : `/collections/${shopAllCategory.slug}`,
    backCTAText: navigationStaticText.back,
    shopAllCTAText: navigationStaticText.shopAll,
    featuredImageModule: null,
    title: localizedShopAllCategory?.title ?? shopAllCategory.title,
    subtitle: localizedShopAllCategory?.subtitle ?? shopAllCategory.subtitle,
    eyebrow: localizedShopAllCategory?.eyebrow ?? shopAllCategory.eyebrow,
    navColumns: [],
  };

  // Return full localized navigation object
  return {
    ...siteNavData,
    categories: [...navigationCategories, shopAllStaticCategory],
    // Request to have a different order for categories on mobile
    mobileCategories: [shopAllStaticCategory, ...navigationCategories],
    // Return localized super banner content
    promoBanner: {
      badge: localizedNavigationInformation?.promoBanner?.badge ?? promoBanner?.badge,
      description:
        localizedNavigationInformation?.promoBanner?.description ??
        promoBanner?.description?.data?.description,
      link:
        getPromoBannerLink(
          localizedNavigationInformation?.promoBanner?.description ??
            promoBanner.description?.data?.description,
          isBlog,
        ) || "",
    },
  };
};

/**
 * Get Strapi Locale Content
 *
 * This function can be used for any module being pulled from strapi to ensure the correct locale is being used.
 *
 * It takes in the current language being used on the site as well as all localizations of the module from strapi
 *
 * Finds the localization to be used and returns its attributes
 *
 * @param {string} language
 * @param {{data: { attributes: StrapiLocaleAttributes<T> }[]}} localizations
 * @returns {attributes: { locale: string, [key: string]: any }}
 */
export const getStrapiLocaleContent = <T,>(
  language: string,
  localizations: { data: { attributes: StrapiLocaleAttributes<T> }[] },
) => {
  return localizations.data.find(
    (item) =>
      strapiLanguagesArray.includes(item.attributes.locale) &&
      item.attributes.locale.includes(language),
  )?.attributes;
};

/**
 * Get Strapi Locale Props
 *
 * This function can be used for any module that is completely localized.
 *
 * It takes in the current locale being used on the site, the english props, and the locale props.
 *
 * The intention here is to return the english props when in locale "en" (US english)
 *
 * Otherwise we use the original props overwritten with localized props. (i.e. {...props, ...localeContent})
 *
 * The locale props needs to be formatted on a case by case basis, as images, relations, and some props have distinct data structures in locale content.
 *
 * @param {string} currentLocale
 * @param {props: T} localizations
 * @returns {localeProps: T}
 */
export const getStrapiLocaleProps = <T,>(
  currentLocale: string,
  props: T,
  localeProps?: T | null,
) => {
  if (currentLocale === "en") return props;
  return localeProps ? localeProps : null;
};

/**
 *
 * Check if rich text is empty
 *
 * We want to enforce types returned from strapi to be null if they do not exist - rich text fields, even if not required,
 * by default return an empty string  - we want to return null in those instances for proper type checking
 *
 * @param text
 * @returns {string | null}
 */
export const checkIfRichTextIsEmpty = (text: string) => {
  return text === "" ? null : text;
};

/**
 * Sanitize Shopify Handle
 *
 * This function is used to sanitize the handle of a product from Shopify
 *
 * Since we have multiple handles tied to a single product, this function
 * can help us remove the extra information from the handle
 *
 * @param {string} handle
 * @returns {string} sanitized handle
 */
export const sanitizeShopifyHandle = (handle: string) => {
  return handle.replace(/(m5-)|(-deal)|(-alum)|(-intl)/g, "");
};

/**
 * RGBA Generator
 *
 * This function will add opacity to a hex color and return the rgba value.
 *
 * Useful so that we can quickly update opacity based colors should the base color change in the future.
 *
 * @param hex
 * @param opacity
 * @returns {string}
 */
export const rbgaGenerator = (hex: string, opacity: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

/**
 * Get Theme
 *
 * This function will return the theme based on the theme value.
 *
 * Useful for setting themes in strapi modules
 *
 * @param theme
 * @returns {object} theme
 */
export const getTheme = (theme?: string) => {
  if (!theme) return lightTheme;
  return theme.toUpperCase() === "DARK" ? darkTheme : lightTheme;
};

/**
 * Text To Required Text
 *
 * Some of our form elements need us to add an asterisk to the label if the field is required.
 *
 * @param text
 * @param required
 * @returns {string}
 */
export const markTextAsRequired = (text: string, required: boolean) => {
  return required ? `${text}*` : text;
};

/**
 * Get Strapi Locale Headline Group
 *
 * Use this function to quickly localize all fields for headline groups when ingesting the strapi component.
 *
 */
export const getStrapiLocaleHeadlineGroup = (
  headlineGroup: StrapiHeadlineProps,
  localeHeadlineGroup?: StrapiLocaleHeadlineProps | null,
): LocaleHeadlineGroupProps => {
  return {
    ...headlineGroup,
    eyebrow: localeHeadlineGroup?.eyebrow ?? headlineGroup.eyebrow,
    headline: localeHeadlineGroup?.headline ?? headlineGroup.headline,
    subtitle: localeHeadlineGroup?.subtitle ?? headlineGroup.subtitle,
    body: localeHeadlineGroup?.body ?? headlineGroup.body?.data.body,
    disclaimer: localeHeadlineGroup?.disclaimer ?? headlineGroup.disclaimer,
    buttons: headlineGroup.buttons
      ? headlineGroup.buttons.map((button, index) => {
          return {
            ...button,
            label: localeHeadlineGroup?.buttons?.[index]?.label ?? button.label,
            ariaLabel: localeHeadlineGroup?.buttons?.[index]?.ariaLabel ?? button.ariaLabel,
          };
        })
      : null,
  };
};
